import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  joinWorkspacePath,
  resolveWorkspaceWindowTitle,
  splitWorkspacePath,
} from '../lib/workspaceTitles';

const WorkspaceContext = createContext(null);

const SESSION_VERSION = 1;
const MIN_BOOT_MS = 900;
const MAX_BOOT_MS = 3500;
const WINDOW_Z_BASE = 85;
const WINDOW_Z_MIN = 100;

function getWorkspaceUserId(user) {
  return user?.user_id || user?.id || user?.email || null;
}

function storageKey(userId) {
  return `osabea_workspace_v${SESSION_VERSION}_${userId || 'anonymous'}`;
}

function createWindowState(path, index = 0, overrides = {}) {
  const { pathname, search } = splitWorkspacePath(path);
  const stagger = (index % 6) * 28;
  return {
    id: overrides.id || `win-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    pathname,
    search,
    title: resolveWorkspaceWindowTitle(pathname, search),
    x: 72 + stagger,
    y: 56 + stagger,
    width: 1040,
    height: 680,
    zIndex: WINDOW_Z_BASE + index,
    minimized: false,
    maximized: false,
    dirty: false,
    dirtyLabel: null,
    refreshKey: 0,
    ...overrides,
  };
}

function normalizePersistedWindow(raw, index) {
  const path = joinWorkspacePath(raw.pathname, raw.search);
  return createWindowState(path, index, {
    id: raw.id,
    title: raw.title || resolveWorkspaceWindowTitle(raw.pathname, raw.search),
    x: Number.isFinite(raw.x) ? raw.x : 72,
    y: Number.isFinite(raw.y) ? raw.y : 56,
    width: Math.max(420, raw.width || 1040),
    height: Math.max(320, raw.height || 680),
    zIndex: Math.max(WINDOW_Z_BASE, raw.zIndex || WINDOW_Z_BASE + index),
    refreshKey: 0,
    minimized: Boolean(raw.minimized),
    maximized: Boolean(raw.maximized),
    dirty: false,
    dirtyLabel: null,
  });
}

export function WorkspaceProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const workspaceUserId = getWorkspaceUserId(user);
  const navigate = useNavigate();
  const location = useLocation();
  const [booting, setBooting] = useState(true);
  const [windows, setWindows] = useState([]);
  const [focusedWindowId, setFocusedWindowId] = useState(null);
  const [pendingClose, setPendingClose] = useState(null);
  const nextZ = useRef(WINDOW_Z_MIN);
  const restoredRef = useRef(false);
  const syncingRef = useRef(false);

  const persistSession = useCallback((nextWindows, nextFocusedId) => {
    if (!workspaceUserId) return;
    try {
      const payload = {
        version: SESSION_VERSION,
        savedAt: new Date().toISOString(),
        focusedWindowId: nextFocusedId,
        windows: nextWindows.map((w) => ({
          id: w.id,
          pathname: w.pathname,
          search: w.search,
          title: w.title,
          x: w.x,
          y: w.y,
          width: w.width,
          height: w.height,
          zIndex: w.zIndex,
          minimized: w.minimized,
          maximized: w.maximized,
        })),
      };
      localStorage.setItem(storageKey(workspaceUserId), JSON.stringify(payload));
    } catch {
      // ignore quota errors
    }
  }, [workspaceUserId]);

  const bringToFront = useCallback((id) => {
    nextZ.current += 1;
    setWindows((prev) => prev.map((w) => (
      w.id === id ? { ...w, zIndex: nextZ.current, minimized: false } : w
    )));
    setFocusedWindowId(id);
  }, []);

  const openWindow = useCallback((path, options = {}) => {
    const fullPath = path.startsWith('/portal') ? path : `/portal/${path.replace(/^\//, '')}`;
    const { pathname, search } = splitWorkspacePath(fullPath);
    const existing = windows.find((w) => w.pathname === pathname && w.search === search);

    if (existing && !options.forceNew) {
      bringToFront(existing.id);
      if (!existing.minimized) {
        const targetPath = joinWorkspacePath(pathname, search);
        const currentPath = joinWorkspacePath(location.pathname, location.search);
        if (targetPath !== currentPath) {
          syncingRef.current = true;
          navigate(targetPath);
        }
      }
      return existing.id;
    }

    const win = createWindowState(fullPath, windows.length, options.overrides);
    nextZ.current += 1;
    win.zIndex = nextZ.current;

    setWindows((prev) => [...prev, win]);
    setFocusedWindowId(win.id);
    syncingRef.current = true;
    navigate(joinWorkspacePath(pathname, search));
    return win.id;
  }, [windows, bringToFront, navigate, location.pathname, location.search]);

  const focusWindow = useCallback((id) => {
    const target = windows.find((w) => w.id === id);
    if (!target) return;
    bringToFront(id);
    const targetPath = joinWorkspacePath(target.pathname, target.search);
    const currentPath = joinWorkspacePath(location.pathname, location.search);
    if (targetPath !== currentPath) {
      syncingRef.current = true;
      navigate(targetPath);
    }
  }, [windows, bringToFront, navigate, location.pathname, location.search]);

  const navigateInFocusedWindow = useCallback((path, options = {}) => {
    const fullPath = path.startsWith('/portal') ? path : `/portal/${path.replace(/^\//, '')}`;
    const { pathname, search } = splitWorkspacePath(fullPath);
    const nextPath = joinWorkspacePath(pathname, search);

    if (focusedWindowId) {
      setWindows((prev) => {
        const next = prev.map((w) => (
          w.id === focusedWindowId
            ? {
              ...w,
              pathname,
              search,
              title: resolveWorkspaceWindowTitle(pathname, search),
            }
            : w
        ));
        persistSession(next, focusedWindowId);
        return next;
      });
    }

    const currentPath = joinWorkspacePath(location.pathname, location.search);
    if (nextPath !== currentPath) {
      syncingRef.current = true;
      navigate(nextPath, options);
    }
  }, [focusedWindowId, location.pathname, location.search, navigate, persistSession]);

  const updateWindowMeta = useCallback((id, patch) => {
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  }, []);

  const setWindowDirty = useCallback((id, dirtyLabel = 'Unsaved changes') => {
    updateWindowMeta(id, { dirty: true, dirtyLabel });
  }, [updateWindowMeta]);

  const clearWindowDirty = useCallback((id) => {
    updateWindowMeta(id, { dirty: false, dirtyLabel: null });
  }, [updateWindowMeta]);

  const refreshWindow = useCallback((id) => {
    setWindows((prev) => prev.map((w) => (
      w.id === id ? { ...w, refreshKey: (w.refreshKey || 0) + 1 } : w
    )));
  }, []);

  const closeWindow = useCallback((id, { force = false } = {}) => {
    const target = windows.find((w) => w.id === id);
    if (!target) return;

    if (target.dirty && !force) {
      setPendingClose({ id, title: target.title, dirtyLabel: target.dirtyLabel });
      return;
    }

    const remaining = windows.filter((w) => w.id !== id);
    setWindows(remaining);

    if (focusedWindowId === id) {
      const next = remaining.filter((w) => !w.minimized).sort((a, b) => b.zIndex - a.zIndex)[0]
        || remaining.sort((a, b) => b.zIndex - a.zIndex)[0];
      if (next) {
        setFocusedWindowId(next.id);
        syncingRef.current = true;
        navigate(joinWorkspacePath(next.pathname, next.search));
      } else {
        setFocusedWindowId(null);
        openWindow('/portal/dashboard');
      }
    }

    persistSession(remaining, focusedWindowId === id ? (remaining[0]?.id || null) : focusedWindowId);
  }, [windows, focusedWindowId, navigate, openWindow, persistSession]);

  const minimizeWindow = useCallback((id) => {
    updateWindowMeta(id, { minimized: true, maximized: false });
  }, [updateWindowMeta]);

  const toggleMaximize = useCallback((id) => {
    setWindows((prev) => prev.map((w) => (
      w.id === id ? { ...w, maximized: !w.maximized, minimized: false } : w
    )));
    const target = windows.find((w) => w.id === id);
    if (target) {
      bringToFront(id);
      const targetPath = joinWorkspacePath(target.pathname, target.search);
      const currentPath = joinWorkspacePath(location.pathname, location.search);
      if (targetPath !== currentPath) {
        syncingRef.current = true;
        navigate(targetPath);
      }
    }
  }, [windows, bringToFront, navigate, location.pathname, location.search]);

  const confirmDiscardClose = useCallback(() => {
    if (!pendingClose) return;
    const { id } = pendingClose;
    setPendingClose(null);
    clearWindowDirty(id);
    closeWindow(id, { force: true });
  }, [pendingClose, clearWindowDirty, closeWindow]);

  const cancelClose = useCallback(() => setPendingClose(null), []);

  // Failsafe: never leave admins stuck on the boot screen
  useEffect(() => {
    const safetyTimer = window.setTimeout(() => setBooting(false), MAX_BOOT_MS);
    return () => window.clearTimeout(safetyTimer);
  }, []);

  // Boot + restore session
  useEffect(() => {
    if (authLoading || !workspaceUserId || restoredRef.current) return;
    restoredRef.current = true;

    const started = Date.now();
    let restoredWindows = [];
    let restoredFocus = null;

    try {
      const raw = localStorage.getItem(storageKey(workspaceUserId));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.windows?.length) {
          restoredWindows = parsed.windows.map((w, i) => normalizePersistedWindow(w, i));
          restoredFocus = parsed.focusedWindowId || restoredWindows[0]?.id;
          const maxZ = Math.max(...restoredWindows.map((w) => w.zIndex || 0), WINDOW_Z_MIN);
          nextZ.current = maxZ;
        }
      }
    } catch {
      restoredWindows = [];
    }

    const finishBoot = () => {
      if (!restoredWindows.length) {
        const initial = createWindowState(joinWorkspacePath(location.pathname, location.search), 0);
        restoredWindows = [initial];
        restoredFocus = initial.id;
      } else {
        const currentPath = joinWorkspacePath(location.pathname, location.search);
        const hasCurrent = restoredWindows.some(
          (w) => joinWorkspacePath(w.pathname, w.search) === currentPath,
        );
        if (!hasCurrent) {
          const extra = createWindowState(currentPath, restoredWindows.length);
          nextZ.current += 1;
          extra.zIndex = nextZ.current;
          restoredWindows.push(extra);
          restoredFocus = extra.id;
        }
      }

      setWindows(restoredWindows);
      setFocusedWindowId(restoredFocus);
      const focused = restoredWindows.find((w) => w.id === restoredFocus) || restoredWindows[0];
      if (focused) {
        syncingRef.current = true;
        navigate(joinWorkspacePath(focused.pathname, focused.search), { replace: true });
      }
      setBooting(false);
    };

    const elapsed = Date.now() - started;
    const delay = Math.max(0, MIN_BOOT_MS - elapsed);
    const timer = window.setTimeout(finishBoot, delay);
    return () => window.clearTimeout(timer);
  }, [authLoading, workspaceUserId, location.pathname, location.search, navigate]);

  // When the browser URL changes (in-app links, browser back/forward), sync the focused window.
  useEffect(() => {
    if (booting) return;
    if (syncingRef.current) {
      syncingRef.current = false;
      return;
    }
    if (!focusedWindowId) return;

    setWindows((prev) => {
      const focused = prev.find((w) => w.id === focusedWindowId);
      if (!focused) return prev;

      const nextPath = joinWorkspacePath(location.pathname, location.search);
      const focusedPath = joinWorkspacePath(focused.pathname, focused.search);
      if (nextPath === focusedPath) return prev;

      const next = prev.map((w) => (
        w.id === focusedWindowId
          ? {
            ...w,
            pathname: location.pathname,
            search: location.search,
            title: resolveWorkspaceWindowTitle(location.pathname, location.search),
          }
          : w
      ));
      persistSession(next, focusedWindowId);
      return next;
    });
  }, [location.pathname, location.search, booting, persistSession, focusedWindowId]);

  // Persist layout changes
  useEffect(() => {
    if (booting || !windows.length) return;
    persistSession(windows, focusedWindowId);
  }, [windows, focusedWindowId, booting, persistSession]);

  const value = useMemo(() => ({
    booting,
    windows,
    focusedWindowId,
    pendingClose,
    openWindow,
    focusWindow,
    navigateInFocusedWindow,
    closeWindow,
    minimizeWindow,
    toggleMaximize,
    updateWindowMeta,
    setWindowDirty,
    clearWindowDirty,
    refreshWindow,
    bringToFront,
    confirmDiscardClose,
    cancelClose,
  }), [
    booting,
    windows,
    focusedWindowId,
    pendingClose,
    openWindow,
    focusWindow,
    navigateInFocusedWindow,
    closeWindow,
    minimizeWindow,
    toggleMaximize,
    updateWindowMeta,
    setWindowDirty,
    clearWindowDirty,
    refreshWindow,
    bringToFront,
    confirmDiscardClose,
    cancelClose,
  ]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return ctx;
}

export function useWorkspaceOptional() {
  return useContext(WorkspaceContext);
}

export function getFocusedWorkspaceLocation(workspace) {
  if (!workspace?.focusedWindowId) return null;
  const win = workspace.windows.find((w) => w.id === workspace.focusedWindowId);
  if (!win) return null;
  return { pathname: win.pathname, search: win.search || '' };
}